<template>
  <v-card
    v-show="selectedEvent"
    class="sm-card my-0"
    flat
    :loading="loading"
  >
    <header class="sm-head">
      <span
        class="sm-dot"
        :style="{ background: selectedEvent.color }"
      />
      <div class="sm-name">
        <v-text-field
          v-if="editName"
          v-model="selectedEvent.name"
          class="input-minimal"
          outlined
          dense
          hide-details
          autofocus
        />
        <template v-else>
          <div class="sm-name-text">
            {{ selectedEvent.name }}
          </div>
          <span
            class="sm-status"
            :class="'sm-status--' + (sessionStatus || 'none')"
          >
            <v-icon size="14">
              {{ sessionStatus === 'done' ? 'mdi-check-circle-outline' : (sessionStatus === 'canceled' ? 'mdi-cancel' : 'mdi-progress-clock') }}
            </v-icon>
            {{ sessionStatus === 'done' ? $t('done') : (sessionStatus === 'canceled' ? $t('canceled') : $t('not-held')) }}
          </span>
        </template>
      </div>
      <div class="sm-head-actions">
        <button
          v-if="!$store.state.accessType"
          type="button"
          class="sm-icon"
          :class="{ 'is-active': editName }"
          :title="$t('edit')"
          @click="editName = !editName"
        >
          <v-icon size="18">
            {{ editName ? 'mdi-check' : 'mdi-pencil-outline' }}
          </v-icon>
        </button>
        <template v-if="!hideBarBtnProp && $store.state.admin === 'active' && !$store.state.accessType">
          <button
            type="button"
            class="sm-icon"
            :title="$t('move')"
            :disabled="(sessionStatus != null ? true : false)"
            @click="$emit('setMoveStatus','move')"
          >
            <v-icon size="18">
              mdi-transfer
            </v-icon>
          </button>
          <button
            type="button"
            class="sm-icon"
            :title="$t('copy')"
            @click="$emit('setMoveStatus','copy')"
          >
            <v-icon size="18">
              mdi-content-copy
            </v-icon>
          </button>
          <button
            type="button"
            class="sm-icon sm-icon--danger"
            :title="$t('remove')"
            :disabled="(sessionStatus != null ? true : false)"
            @click="$emit('removeEvent')"
          >
            <v-icon size="18">
              mdi-delete-outline
            </v-icon>
          </button>
        </template>
        <button
          type="button"
          class="sm-icon"
          :title="$t('close')"
          @click="$emit('close')"
        >
          <v-icon size="18">
            mdi-close
          </v-icon>
        </button>
      </div>
    </header>

    <div
      v-if="session"
      class="sm-body"
    >
      <div class="sm-facts">
        <div class="sm-fact">
          <v-icon size="18">
            mdi-calendar-blank-outline
          </v-icon>
          <span>{{ session.jdate }}</span>
        </div>
        <div
          v-if="info"
          class="sm-fact"
        >
          <v-icon size="18">
            mdi-clock-outline
          </v-icon>
          <span class="sm-ltr">{{ session.start_time }}</span>
          <span class="sm-muted">· {{ $t('duration') }} <span class="sm-ltr">{{ session.duration }}</span></span>
        </div>
        <div class="sm-fact">
          <v-icon size="18">
            mdi-office-building-outline
          </v-icon>
          <span>{{ $t('place') }} {{ $t('class') }} {{ (selectedEvent.place ? selectedEvent.place.name :'') }}</span>
        </div>
        <div
          v-if="info && session.event.type == 'interview'"
          class="sm-fact"
        >
          <v-icon size="18">
            mdi-account-outline
          </v-icon>
          <span>{{ $t('student', {name: $store.state.studentLabel}) }}: {{ (session.student ? session.student.name : '') }}</span>
        </div>
        <div
          v-if="info && !session.event.type"
          class="sm-fact"
        >
          <v-icon size="18">
            mdi-account-group-outline
          </v-icon>
          <span>{{ $t('studentCount', {name: $store.state.studentLabel}) }}: <b>{{ info.studentCount }}</b></span>
        </div>
      </div>

      <div
        v-if="info && !session.event.type"
        class="sm-progress"
      >
        <div>{{ $t('sessionTotal') }}: <b>{{ info.total }}</b></div>
        <div class="sm-bar">
          <span
            class="sm-bar-done"
            :style="{ width: pct(info.done) }"
          />
          <span
            class="sm-bar-cancel"
            :style="{ width: pct(info.canceled) }"
          />
        </div>
        <div class="sm-legend">
          <span><i class="sm-key sm-key--done" />{{ $t('sessionDone') }}: <b>{{ info.done }}</b></span>
          <span><i class="sm-key sm-key--wait" />{{ $t('sessionNotHeld') }}: <b>{{ info.not_held }}</b></span>
          <span><i class="sm-key sm-key--cancel" />{{ $t('sessionCancel') }}: <b>{{ info.canceled }}</b></span>
        </div>
      </div>

      <div class="sm-fields">
        <div class="input-minimal">
          <autocomplete
            v-model="teacher"
            title="teacher"
            type="person"
            :new-btn="true"
            :disabled="(sessionStatus || $store.state.accessType ? true : false)"
            :data="JSON.stringify({type: 'teacher'})"
            :value="teacher"
          />
        </div>
        <div
          v-if="!sessionStatus && !$store.state.accessType"
          class="input-minimal"
        >
          <money
            v-model="teacherCost"
            :label="$t('teacherCost') + '(' + $t('perSession') + ')'"
            :dense="true"
            :outlined="true"
            :solo="false"
            :clearable="true"
            value-when-is-empty=""
            :options="options"
            class="dense"
          />
        </div>
        <div
          v-if="!$store.state.accessType"
          class="input-minimal"
        >
          <v-select
            v-model="sessionStatus"
            :label="$t('status')"
            :items="statusItems"
            dense
            outlined
            hide-details
            :loading="loading"
            item-value="value"
            item-text="text"
            @change="setSessionStatus"
          />
        </div>
      </div>

      <div class="sm-links">
        <button
          v-if="!session.event.type"
          type="button"
          class="sm-link"
          :disabled="(sessionStatus && $store.state.accessType ? true : false)"
          @click="goAttendance"
        >
          <v-icon size="18">
            mdi-clipboard-check-outline
          </v-icon>
          <span>{{ $t('academyStudentAttendance', {name: $store.state.studentLabel}) }}</span>
          <v-icon
            size="16"
            class="sm-link-go"
          >
            mdi-chevron-left
          </v-icon>
        </button>
        <button
          v-if="!$store.state.accessType"
          type="button"
          class="sm-link"
          @click="eventStudentList"
        >
          <v-icon size="18">
            mdi-account-group-outline
          </v-icon>
          <span>{{ $t('academyEventStudentList', {name: $store.state.studentLabel}) }}</span>
          <v-icon
            size="16"
            class="sm-link-go"
          >
            mdi-chevron-left
          </v-icon>
        </button>
        <button
          v-if="!$store.state.accessType"
          type="button"
          class="sm-link"
          @click="studentList"
        >
          <v-icon size="18">
            mdi-account-plus-outline
          </v-icon>
          <span>{{ $t('addStudentToEvent', {name: $store.state.studentLabel}) }}</span>
          <v-icon
            size="16"
            class="sm-link-go"
          >
            mdi-chevron-left
          </v-icon>
        </button>
        <button
          v-if="!$store.state.accessType && session && !session.event.type"
          type="button"
          class="sm-link"
          @click="sessionList"
        >
          <v-icon size="18">
            mdi-calendar-text-outline
          </v-icon>
          <span>{{ $t('academySessionList') }}</span>
          <v-icon
            size="16"
            class="sm-link-go"
          >
            mdi-chevron-left
          </v-icon>
        </button>
      </div>

      <div
        v-if="(!session.status || session.status == 'canceled') && !$store.state.accessType"
        class="sm-sms"
      >
        <button
          v-if="!session.status && !$store.state.accessType"
          type="button"
          class="sm-sms-btn sm-sms-btn--green"
          @click="sendStatusSms('sessionHelding')"
        >
          <v-icon size="16">
            mdi-message-processing-outline
          </v-icon>
          {{ $t('sendHeldingStatusSms') }}
        </button>
        <button
          v-if="session.status == 'canceled' && !$store.state.accessType"
          type="button"
          class="sm-sms-btn sm-sms-btn--red"
          @click="sendStatusSms('sessionCanceld')"
        >
          <v-icon size="16">
            mdi-message-processing-outline
          </v-icon>
          {{ $t('sendCancelStatusSms') }}
        </button>
      </div>
    </div>

    <footer class="sm-foot">
      <v-btn
        v-if="info && !$store.state.accessType"
        color="primary"
        depressed
        class="app-form-action-btn"
        @click="teacherUpdate"
      >
        {{ $t('submit') }}
      </v-btn>
      <v-spacer />
      <v-btn
        color="grey"
        outlined
        class="app-form-action-btn"
        @click="$emit('close')"
      >
        {{ $t('close') }}
      </v-btn>
    </footer>
    <v-snackbar
      v-model="snakbar"
      :color="(snakbarColor ? snakbarColor : '')"
      top
      style="z-index: 1000000000000 !important;"
    >
      {{ snakbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snakbar = !snakbar"
        >
          {{ $t('close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
  // Local copy of ./sessionModal.vue (shared with calender.vue) restyled for the academy dashboard.
  import General from '../../../../../Mixins/general'
  export default {
    name: 'AcademyDashboardSessionModal',
    components: {
    },
    mixins: [General],
    props: ['selectedEvent', 'hideBarBtnProp'],
    data: () => ({
      editName: null,
      teacherCost: null,
      sessionStatus: null,
      statusItems: null,
      teacher: null,
      teacherOld: null,
      render: false,
      isLoading: false,
      status: 'active',
      address: null,
      name: null,
      session: null,
      info: null,
      valid: null,
      message: null,
      id: null,
      loading: false,
    }),
    watch: {
      selectedEvent (val, old) {
        this.changeSelectedEvent(val)
      },
      sessionStatus (val) {
        // this.setSessionStatus()
      },
    },
    beforeMount () {
      this.statusItems = [
        {
          value: null,
          text: this.$t('not-held'),
        },
        {
          value: 'done',
          text: this.$t('done'),
        },
        {
          value: 'canceled',
          text: this.$t('canceled'),
        },
      ]
    },
    mounted () {
      if (this.selectedEvent) {
        this.changeSelectedEvent(this.selectedEvent)
      }
    },
    methods: {
      pct (n) {
        const total = this.info && +this.info.total
        return total ? Math.min(100, (+n || 0) / total * 100) + '%' : '0%'
      },
      sessionList () {
        this.$router.push({ name: 'academySessionList', params: { eventProp: this.session.event_id } })
      },
      sendStatusSms (type) {
        var app = this
        app.loading = true
        app.clearing = true
        app.showing = true
        app.$axios.get(this.$endpoint + 'academySession', {
          params: {
            sendStatusSms: 'active',
            session: app.session,
            type: type,
          },
        })
          .then(res => {
            res = res.data
            app.snakbarColor = 'green'
            app.snakbarText = this.$t('sent')
            app.snakbar = true
          })
          .catch(err => {
            app.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
      changeSelectedEvent (val) {
        this.session = null
        this.sessionStatus = null
        this.info = null
        this.teacher = val.teacher
        this.teacherOld = val.teacher
        this.teacherCost = val.teacher_cost
        if (val.id) {
          this.getEventInfo(val.id)
        }
      },
      allowedStep: m => m % 5 === 0,
      create () {
        var app = this
        app.isLoading = true
        app.$axios.post(this.$endpoint + 'academyPlaces', app.$data)
          .then(res => {
            app.goTo('academyPlaceList')
          })
          .catch(err => {
            this.serverErrorHandeler(err)
          })
          .finally(() => (this.isLoading = false))
      },
      goAttendance () {
        this.$router.push({ name: 'academyStudentAttendance', params: { sessionProp: this.session } })
      },
      studentList () {
        this.$router.push({ name: 'academyEventStudent', params: { eventProp: this.session.event, sessionProp: (this.session.event.type === 'interview' ? this.session : null) } })
      },
      getEventInfo (id) {
        var app = this
        app.loading = true
        app.clearing = true
        app.showing = true
        app.$axios.get(this.$endpoint + 'academySession', {
          params: {
            getEventInfo: 'active',
            sessionId: id,
          },
        })
          .then(res => {
            res = res.data
            app.info = res.info
            app.session = res.session
            app.sessionStatus = res.session.status
          })
          .catch(err => {
            app.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
      setSessionStatus () {
        var app = this
        app.loading = true
        app.clearing = true
        app.showing = true
        app.$axios.get(this.$endpoint + 'academySession', {
          params: {
            setSessionStatus: 'active',
            status: app.sessionStatus,
            session: app.session,
          },
        })
          .then(res => {
            res = res.data
            app.session.status = app.sessionStatus
            app.snakbarColor = 'green'
            app.snakbarText = this.$t('alert-created')
            app.snakbar = true
            app.$emit('updateStatus', app.sessionStatus)
          })
          .catch(err => {
            app.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
      teacherUpdate () {
        var app = this
        app.loading = true
        app.clearing = true
        app.showing = true
        app.$axios.get(this.$endpoint + 'academySession', {
          params: {
            teacherUpdate: 'active',
            teacher: app.teacher,
            teacherCost: app.teacherCost,
            event: app.selectedEvent,
          },
        })
          .then(res => {
            res = res.data
            var data = {
              event: app.selectedEvent,
              teacher: app.teacher,
              teacher_cost: app.teacherCost,
            }
            app.editName = false
            app.$emit('updateTeacher', data)
            app.$emit('close')
          })
          .catch(err => {
            app.teacher = app.teacherOld
            app.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
      eventStudentList (item) {
        this.$router.push({ name: 'academyEventStudentList', params: { eventProp: this.session.event } })
      },
      update () {
        var app = this
        app.isLoading = true
        app.$axios.put(this.$endpoint + 'academyPlaces/' + this.$route.params.id, this.$data)
          .then(res => {
            res = res.data
            app.$emit('close', app.$route)
            app.goTo('academyPlaceList')
          })
          .catch(err => {
            app.serverErrorHandeler(err)
          })
          .finally(() => (app.isLoading = false))
      },
    },
  }
</script>

<style scoped>

.v-event-timed {
  user-select: none;
  -webkit-user-select: none;
}

.v-event-drag-bottom {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 4px;
  height: 4px;
  cursor: ns-resize;
}
.v-event-drag-bottom::after {
  display: none;
  position: absolute;
  left: 50%;
  height: 4px;
  border-top: 1px solid white;
  border-bottom: 1px solid white;
  width: 16px;
  margin-left: -8px;
  opacity: 0.8;
  content: '';
}

.v-event-drag-bottom:hover::after {
  display: block;
}
.v-event-draggable {
  white-space: normal !important;
  padding: 3px 5px;
  font-size: 11px;
}

</style>

<style scoped lang="scss">
.sm-card { border-radius: 6px !important; overflow: hidden; }
.sm-head {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  padding-block: 8px;
  padding-inline: 16px 10px;
  border-bottom: 1px solid rgba(15, 23, 42, .07);
}
.sm-dot { flex-shrink: 0; width: 12px; height: 12px; border-radius: 50%; }
.sm-name {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.sm-name-text {
  font-weight: 600;
  font-size: .92rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sm-status {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px;
  border-radius: 6px;
  font-size: .72rem;
  .v-icon { color: inherit; }
  &--none { background: rgba(25, 118, 210, .1); color: var(--v-primary-base, #1976d2); }
  &--done { background: rgba(46, 125, 50, .12); color: #2e7d32; }
  &--canceled { background: rgba(194, 24, 91, .1); color: #c2185b; }
}
.sm-head-actions { display: flex; gap: 4px; flex-shrink: 0; }
.sm-icon {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: rgba(15, 23, 42, .04);
  color: rgba(15, 23, 42, .7);
  .v-icon { color: inherit; }
  &:hover, &.is-active { background: rgba(25, 118, 210, .1); color: var(--v-primary-base, #1976d2); }
  &--danger:hover { background: rgba(194, 24, 91, .1); color: #c2185b; }
  &:disabled { opacity: .35; pointer-events: none; }
}
.sm-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 16px;
  background: #f3f7fc;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
  overflow-x: hidden;
  > * { flex-shrink: 0; }
}
.sm-facts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.sm-fact {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 6px;
  font-size: .8rem;
  .v-icon { color: var(--v-primary-base, #1976d2); }
}
.sm-muted { opacity: .6; }
.sm-ltr { direction: ltr; unicode-bidi: isolate; }
.sm-progress {
  padding: 10px 12px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 6px;
  font-size: .8rem;
}
.sm-bar {
  display: flex;
  height: 6px;
  margin: 8px 0;
  border-radius: 6px;
  overflow: hidden;
  background: rgba(57, 73, 171, .15);
}
.sm-bar-done { background: #43a047; }
.sm-bar-cancel { background: #e91e63; }
.sm-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  font-size: .75rem;
  opacity: .85;
}
.sm-key {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-inline-end: 5px;
  border-radius: 50%;
  &--done { background: #43a047; }
  &--wait { background: rgba(57, 73, 171, .45); }
  &--cancel { background: #e91e63; }
}
.sm-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 10px;
  padding-top: 6px;
  ::v-deep .v-input { margin: 0; padding: 0; height: auto !important; }
  ::v-deep .v-text-field__details { display: none; }
}
.sm-links {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.sm-link {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 0 10px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 6px;
  font-size: .8rem;
  text-align: start;
  .v-icon { color: var(--v-primary-base, #1976d2); }
  span { flex: 1; min-width: 0; }
  .sm-link-go { opacity: .5; }
  &:hover { border-color: rgba(25, 118, 210, .35); background: rgba(25, 118, 210, .04); }
  &:disabled { opacity: .4; pointer-events: none; }
}
.sm-sms { display: flex; flex-wrap: wrap; gap: 8px; }
.sm-sms-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: .78rem;
  .v-icon { color: inherit; }
  &--green { background: rgba(46, 125, 50, .1); color: #2e7d32; }
  &--red { background: rgba(194, 24, 91, .1); color: #c2185b; }
}
.sm-foot {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid rgba(15, 23, 42, .07);
}
@media (max-width: 799px) {
  .sm-facts, .sm-fields, .sm-links { grid-template-columns: 1fr; }
  .sm-body { max-height: none; }
}
.theme--dark {
  .sm-head, .sm-foot { border-color: rgba(255, 255, 255, .08); }
  .sm-body { background: #121212; }
  .sm-fact, .sm-progress, .sm-link { background: #1e1e1e; border-color: rgba(255, 255, 255, .08); }
  .sm-icon { background: rgba(255, 255, 255, .06); color: rgba(255, 255, 255, .8); }
  .sm-fact .v-icon, .sm-link .v-icon, .sm-status--none { color: #90caf9; }
}
</style>
